import React from 'react';
import { FiCheckCircle } from 'react-icons/fi';

const Quality = () => {
  const coaImages = [
    'https://customer-assets.emergentagent.com/job_aps-research/artifacts/og5554iw_image.png',
    'https://customer-assets.emergentagent.com/job_aps-research/artifacts/70puutxy_image.png',
    'https://customer-assets.emergentagent.com/job_aps-research/artifacts/2ybl75h8_image.png',
    'https://customer-assets.emergentagent.com/job_aps-research/artifacts/pwy10jof_image.png',
  ];

  const testResults = [
    { compound: 'Selank', batch: '2026.1.28', purity: '99.806%' },
    { compound: 'Tirzepatide', batch: '2025.11.28', purity: '99.759%' },
    { compound: 'Retatrutide', batch: '2025.11.28', purity: '99.404%' },
    { compound: 'GHK-Cu, BPC-157, TB-500', batch: '2025.11.28', purity: 'Multi-peptide verified' },
  ];

  return (
    <div className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-4xl mb-16">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tighter text-primary-main mb-6" data-testid="quality-heading">
            Quality & Testing Standards
          </h1>
          <p className="text-lg text-text-secondary leading-relaxed">
            Every batch of peptides is independently tested by reputable third-party laboratories. 
            We provide complete transparency through accessible Certificates of Analysis.
          </p>
        </div>

        {/* Testing Process */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <div className="bg-accent-subtle w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <FiCheckCircle className="w-6 h-6 text-accent-main" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-primary-main mb-3">Independent Testing</h3>
            <p className="text-sm text-text-secondary">
              All products are tested by accredited third-party laboratories including Janoshik Analytical, 
              ensuring unbiased verification of purity and composition.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <div className="bg-accent-subtle w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <FiCheckCircle className="w-6 h-6 text-accent-main" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-primary-main mb-3">Batch Verification</h3>
            <p className="text-sm text-text-secondary">
              Each product is linked to specific batch numbers with corresponding test results. 
              Traceability is maintained throughout the entire supply chain.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <div className="bg-accent-subtle w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <FiCheckCircle className="w-6 h-6 text-accent-main" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-primary-main mb-3">QR Verification</h3>
            <p className="text-sm text-text-secondary">
              COA documents include QR codes that can be scanned to verify authenticity directly 
              with the testing laboratory's database.
            </p>
          </div>
        </div>

        {/* Test Results */}
        <div className="mb-16">
          <h2 className="text-3xl font-heading font-bold text-primary-main mb-8">Recent Test Results</h2>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background-subtle">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Compound</th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Batch Number</th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Purity</th>
                  </tr>
                </thead>
                <tbody>
                  {testResults.map((result, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-6 py-4 text-sm text-text-primary">{result.compound}</td>
                      <td className="px-6 py-4 text-sm font-mono text-text-secondary">{result.batch}</td>
                      <td className="px-6 py-4 text-sm font-mono font-medium text-success">{result.purity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* COA Gallery */}
        <div>
          <h2 className="text-3xl font-heading font-bold text-primary-main mb-8">Sample Certificates of Analysis</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coaImages.map((img, idx) => (
              <div
                key={idx}
                className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => window.open(img, '_blank')}
                data-testid={`coa-sample-${idx}`}
              >
                <img src={img} alt={`COA ${idx + 1}`} className="w-full h-auto rounded" />
                <p className="text-center text-sm text-text-muted mt-4">Click to view full report</p>
              </div>
            ))}
          </div>
        </div>

        {/* Standards */}
        <div className="mt-16 bg-background-subtle border border-slate-200 rounded-lg p-8">
          <h3 className="text-2xl font-heading font-semibold text-primary-main mb-6">Our Quality Standards</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <FiCheckCircle className="text-success w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-text-secondary">Minimum ≥98% purity across all products</span>
            </li>
            <li className="flex items-start gap-3">
              <FiCheckCircle className="text-success w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-text-secondary">HPLC, MS, and UV analysis performed by certified laboratories</span>
            </li>
            <li className="flex items-start gap-3">
              <FiCheckCircle className="text-success w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-text-secondary">Complete batch traceability and documentation</span>
            </li>
            <li className="flex items-start gap-3">
              <FiCheckCircle className="text-success w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-text-secondary">Proper storage and handling protocols throughout supply chain</span>
            </li>
            <li className="flex items-start gap-3">
              <FiCheckCircle className="text-success w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-text-secondary">Regular quality audits and continuous improvement processes</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Quality;
